import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, CheckCircle2, BellOff, AlertTriangle, RefreshCw } from 'lucide-react';
import useAutoRefresh from '../../lib/useAutoRefresh';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Today's WhatsApp activity rolled up into 3 actionable buckets so the
 * admin can see at a glance whether the master toggle changes are
 * doing what they expect:
 *
 *   • Sent     — actually delivered to whatapi.in (HTTP 2xx)
 *   • Skipped  — master-toggle silenced (uncheck-all in WhatsApp Settings)
 *   • Failed   — whatapi.in returned a 4xx/5xx, or network error
 *
 * A per-template breakdown is rendered below the totals so the admin can
 * pinpoint exactly which template is generating the noise.
 */
export default function WhatsappActivityWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchActivity = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/whatsapp-activity`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) setData(await r.json());
    } catch (e) {
      console.error('[WhatsappActivityWidget]', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchActivity(); }, [fetchActivity]);
  useAutoRefresh(() => fetchActivity(), 60_000);

  const totals = data?.totals || { sent: 0, skipped: 0, failed: 0, total: 0 };
  const top = (data?.by_template || []).slice(0, 5);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="whatsapp-activity-widget">
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare size={16} className="text-emerald-600" />
          <h3 className="text-sm font-semibold text-gray-900">WhatsApp Activity — Today</h3>
        </div>
        <button
          type="button"
          onClick={fetchActivity}
          className="text-xs text-gray-500 hover:text-gray-800 inline-flex items-center gap-1"
          data-testid="whatsapp-activity-refresh"
          title="Refresh now"
        >
          <RefreshCw size={12} /> {loading ? 'Loading' : 'Refresh'}
        </button>
      </div>

      <div className="grid grid-cols-3 divide-x divide-gray-100">
        <ActivityCell
          label="Sent"
          value={totals.sent}
          icon={CheckCircle2}
          tone="emerald"
          testid="whatsapp-activity-sent"
        />
        <ActivityCell
          label="Skipped (silenced)"
          value={totals.skipped}
          icon={BellOff}
          tone="amber"
          testid="whatsapp-activity-skipped"
        />
        <ActivityCell
          label="Failed"
          value={totals.failed}
          icon={AlertTriangle}
          tone="red"
          testid="whatsapp-activity-failed"
        />
      </div>

      {top.length > 0 && (
        <div className="border-t bg-gray-50/60 px-4 py-2">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5">
            Top templates today
          </p>
          <ul className="space-y-1" data-testid="whatsapp-activity-breakdown">
            {top.map(row => (
              <li
                key={row.template}
                className="flex items-center justify-between text-xs"
                data-testid={`whatsapp-activity-row-${row.template}`}
              >
                <span className="text-gray-700 font-medium truncate max-w-[200px]" title={row.template}>
                  {row.template}
                </span>
                <span className="flex items-center gap-2 tabular-nums">
                  <span className="text-emerald-700">{row.sent}</span>
                  <span className="text-gray-300">·</span>
                  <span className="text-amber-700">{row.skipped}</span>
                  <span className="text-gray-300">·</span>
                  <span className="text-red-700">{row.failed}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {totals.total === 0 && !loading && (
        <div className="px-4 py-6 text-center text-xs text-gray-400" data-testid="whatsapp-activity-empty">
          No WhatsApp activity recorded today.
        </div>
      )}
    </div>
  );
}

const TONE_BG = { emerald: 'bg-emerald-50', amber: 'bg-amber-50', red: 'bg-red-50' };
const TONE_TEXT = { emerald: 'text-emerald-700', amber: 'text-amber-700', red: 'text-red-700' };
const TONE_ICON = { emerald: 'text-emerald-500', amber: 'text-amber-500', red: 'text-red-500' };

function ActivityCell({ label, value, icon: Icon, tone, testid }) {
  return (
    <div className={`px-4 py-3 ${TONE_BG[tone]}`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <p className="text-[11px] text-gray-600 font-medium uppercase tracking-wider">{label}</p>
        <Icon size={14} className={TONE_ICON[tone]} />
      </div>
      <p className={`text-2xl font-bold mt-1 tabular-nums ${TONE_TEXT[tone]}`}>{value}</p>
    </div>
  );
}
